import axios from "axios";
import { getEnhancedErrorMessage } from "../utils/validation";
import { ERRORS } from "../constants/errorMessages";
/**
 * Error thrown when an API request returns a 401 Unauthorized status.
 * This error is caught centrally and should not be logged to Rollbar,
 * as 401s are expected auth failures (session expiry, token refresh race conditions).
 */
export class UnauthorizedError extends Error {
    constructor() {
        super();
        this.status = 401;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, UnauthorizedError);
        }
        this.name = "UnauthorizedError";
        this.message = "Unauthorized - session may have expired";
    }
}
/**
 * Base class for MadSDK API errors.
 *
 * Provides two behaviors:
 * 1. **401 interception**: If the original error is a 401, throws UnauthorizedError
 *    instead of the module-specific error. This prevents 401s from polluting Rollbar.
 * 2. **Detailed error messages**: Uses getEnhancedErrorMessage to generate user-friendly
 *    messages based on the error type (timeout, network, auth, HTTP status, etc.).
 *
 * Subclasses provide a `context` string (e.g., "Failed to fetch recipe(s).") that is
 * prepended to the detailed message so users always see what operation failed.
 *
 * Usage:
 *   class RecipeFetchFailed extends MadSDKAPIError {
 *     constructor(apiError: DoNotCare) {
 *       super(apiError, "Failed to fetch recipe(s).");
 *       this.name = "RecipeFetchFailed";
 *     }
 *   }
 */
export class MadSDKAPIError extends Error {
    constructor(originalError, context, fallbackMessage = ERRORS.GENERAL.FALLBACK_MESSAGE) {
        super();
        if (axios.isAxiosError(originalError) && originalError.response?.status === 401) {
            throw new UnauthorizedError();
        }
        if (context) {
            this.message = `${context} ${getEnhancedErrorMessage(originalError, fallbackMessage)}`;
        }
    }
}
//# sourceMappingURL=base.js.map